/**
 * RA2 dist datepicker port: same logic as RA2 public/dist/datepicker.js.
 * 3 views (years → months → days), 和暦, startDate/endDate, holidays (RA2 format).
 */
import { AppTextField } from '@/components/atoms';
import { useOptionalTheme } from '@shared/providers/theme';
import dayjs from 'dayjs';
import { convGtJDateShort } from '@/utils/dateConverter';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { JA_JP, VIEWS, YEARS_RANGE, YEAR_BOUNDARIES } from './constants';
import { useJpHolidays } from '@/services/main/jpHolidays';
import {
  canAppendWarekiDigit,
  checkHolidayForMonth,
  dateToRawDigits,
  formatDate,
  formatWarekiProgressive,
  getDaysInMonth,
  getMinDay,
  getWarekiMaxDigits,
  isAtSuffixBoundary,
  isDayValid,
  isMonthValid,
  parseDate,
  parseFormat,
  parseWarekiShorthand,
  convertWareki,
  convertWarekiMonth,
  convertSeireki,
} from './datepickerLogic';
import { InputRef } from 'antd';

/** Holidays: { "YYYY-MM": ["01","15",...] } - RA2 CommonClass::getHolidays() format */
export type HolidaysMap = Record<string, string[]>;
type DefaultEra = 'reiwa';

export type RA2NDatePickerProps = {
  value?: string | null;
  onChange?: (value: string) => void;
  /** 'day' | 'month' | 'year' - day picker (yyyy年mm月dd日), month picker (yyyy年mm月), or year picker (yyyy年) */
  picker?: 'day' | 'month' | 'year';
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  holidays?: HolidaysMap;
  autoHide?: boolean;
  /** 0=days, 1=months, 2=years */
  startView?: 0 | 1 | 2;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  /** Controls popup placement. Use 'bottom' when the calendar must stay under the input. */
  popupPlacement?: 'auto' | 'bottom';
  /** Optional callback when user presses Enter while focused on the input. */
  onEnter?: () => void;
  /** Optional callback fired when the calendar popup opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** If true, users can only select from the calendar and cannot type/paste directly. */
  readOnlyInput?: boolean;
  /** Optional callback fired when the picker commits the final calendar selection. */
  onCalendarCommit?: (value: string) => void;
  /** If true, displays date in short wareki format (e.g. R08.01.18) when blurred */
  shortFormat?: boolean;
  /** Optional era prefix to prefill while keeping the actual form value empty. */
  defaultEra?: DefaultEra;
  /** Custom format string for year picker (defaults to JA_JP.formatYear i.e. 'yyyy年') */
  yearFormat?: string;
};

const DEFAULT_START_DATE = '1868-01-25';
const DEFAULT_END_DATE = '2057-12-31'; // up to 令和39
const INCOMPLETE_ERA_PREFIX = /^(明|大|昭|平|令)$/;
const DEFAULT_ERA_RAW_DIGITS: Record<DefaultEra, string> = {
  reiwa: '5',
};
const HALF_WIDTH_ALPHANUMERIC_STYLE = {
  imeMode: 'disabled',
} as React.CSSProperties;

/** Era letter shortcuts (half/fullwidth) → full era name */
const ERA_LETTER_MAP: Record<string, string> = {
  m: '明治',
  M: '明治',
  ｍ: '明治',
  Ｍ: '明治',
  t: '大正',
  T: '大正',
  ｔ: '大正',
  Ｔ: '大正',
  s: '昭和',
  S: '昭和',
  ｓ: '昭和',
  Ｓ: '昭和',
  h: '平成',
  H: '平成',
  ｈ: '平成',
  Ｈ: '平成',
  r: '令和',
  R: '令和',
  ｒ: '令和',
  Ｒ: '令和',
};

/** Normalize pasted text: fullwidth digits→halfwidth, era letter shortcuts→era name */
function normalizePastedWareki(text: string): string {
  // Fullwidth digits → halfwidth
  let s = text.replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  // Fullwidth 年月日 → halfwidth (these are already fullwidth kanji, keep as-is)
  // Era letter at start → era name
  if (s.length > 0 && ERA_LETTER_MAP[s[0]]) {
    s = ERA_LETTER_MAP[s[0]] + s.slice(1);
  }
  return s;
}

/** Check if text looks like a partial wareki: era name + optional digits/年月日 */
const PARTIAL_WAREKI_RE = /^(明治|大正|昭和|平成|令和)(\d{0,2}(年(\d{0,2}(月(\d{0,2}日?)?)?)?)?)?$/;

/**
 * Map a single inserted character to an era digit (1..5) or numeric digit.
 * Used by the Android onChange typing path — Android Chrome's virtual keyboard
 * fires keydown with key='Unidentified' / keyCode=229 / empty e.code, so the
 * onKeyDown era/digit maps don't fire and we must reconstruct the digit from
 * the character that landed in the input.
 */
const ERA_NAME_TO_DIGIT: Record<string, string> = {
  明治: '1',
  大正: '2',
  昭和: '3',
  平成: '4',
  令和: '5',
};

/**
 * Parse a partial-wareki edit string (e.g. '令和07年01月3' / '令和07年01月') back
 * into the EYYMMDD raw-digit form so subsequent typing can continue progressive
 * conversion instead of being trapped in free-edit inputBuffer mode. Returns
 * { digits, hideSuffix } where hideSuffix mirrors the suffix-presence state
 * used by formatWarekiProgressive.
 */
function partialWarekiToRawDigits(
  text: string
): { digits: string; hideSuffix: boolean } | null {
  const m = text.match(
    /^(明治|大正|昭和|平成|令和)(\d{0,2})(年)?(\d{0,2})(月)?(\d{0,2})(日)?$/
  );
  if (!m) return null;
  const eraDigit = ERA_NAME_TO_DIGIT[m[1]];
  if (!eraDigit) return null;
  const yy = m[2] || '';
  const hasYear = !!m[3];
  const mm = m[4] || '';
  const hasMonth = !!m[5];
  const dd = m[6] || '';
  const hasDay = !!m[7];
  // Reject malformed like '令和年' (suffix without preceding digits) or
  // '令和07月03' (month without year suffix).
  if (hasYear && yy.length === 0) return null;
  if (hasMonth && (mm.length === 0 || !hasYear)) return null;
  if (hasDay && (dd.length === 0 || !hasMonth)) return null;
  if ((mm.length > 0 || hasMonth) && !hasYear) return null;
  if ((dd.length > 0 || hasDay) && !hasMonth) return null;
  const digits = eraDigit + yy + mm + dd;
  let hideSuffix = false;
  if (digits.length === 3 && !hasYear) hideSuffix = true;
  else if (digits.length === 5 && !hasMonth) hideSuffix = true;
  else if (digits.length === 7 && !hasDay) hideSuffix = true;
  return { digits, hideSuffix };
}

const ANDROID_CHAR_TO_DIGIT: Record<string, string> = {
  '0': '0', '1': '1', '2': '2', '3': '3', '4': '4',
  '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
  '０': '0', '１': '1', '２': '2', '３': '3', '４': '4',
  '５': '5', '６': '6', '７': '7', '８': '8', '９': '9',
  m: '1', M: '1', ｍ: '1', Ｍ: '1',
  t: '2', T: '2', ｔ: '2', Ｔ: '2',
  s: '3', S: '3', ｓ: '3', Ｓ: '3',
  h: '4', H: '4', ｈ: '4', Ｈ: '4',
  r: '5', R: '5', ｒ: '5', Ｒ: '5',
  '!': '1', '@': '2', '#': '3', $: '4', '%': '5',
};

function createItem(
  classes: string[],
  view: string,
  text: React.ReactNode,
  key?: React.Key
): React.ReactNode {
  return (
    <li
      key={key ?? `${view}-${String(text)}`}
      className={classes.filter(Boolean).join(' ')}
      data-view={view}
    >
      {text}
    </li>
  );
}

const RA2NDatePicker = React.forwardRef<InputRef, RA2NDatePickerProps>(function RA2NDatePicker(
  {
    value = '',
    onChange,
    picker = 'day',
    startDate: startDateProp,
    endDate: endDateProp,
    holidays = {},
    autoHide = true,
    startView,
    placeholder,
    disabled = false,
    className,
    error = false,
    popupPlacement = 'auto',
    onEnter,
    onOpenChange,
    readOnlyInput = false,
    onCalendarCommit,
    shortFormat = false,
    defaultEra,
    yearFormat,
  },
  externalRef
) {
  const theme = useOptionalTheme();
  const colors = theme?.colors;
  const formatStr =
    picker === 'year' ? (yearFormat ?? JA_JP.formatYear) : picker === 'month' ? JA_JP.formatMonth : JA_JP.format;
  const format = useMemo(() => parseFormat(formatStr), [formatStr]);
  const effectiveStartView =
    startView ?? (picker === 'year' ? VIEWS.YEARS : picker === 'month' ? VIEWS.MONTHS : VIEWS.DAYS);

  // Default date range (unless explicitly passed as null/Date/string by caller)
  const effectiveStartDateProp = startDateProp === undefined ? DEFAULT_START_DATE : startDateProp;
  const effectiveEndDateProp = endDateProp === undefined ? DEFAULT_END_DATE : endDateProp;

  const [date, setDate] = useState<Date>(() => parseDate(value || null, format));
  const [viewDate, setViewDate] = useState<Date>(() => new Date(date));
  const japanHolidays = useJpHolidays(viewDate.getFullYear());
  const mergedHolidays = useMemo<HolidaysMap>(() => {
    const merged = { ...japanHolidays };
    for (const [key, days] of Object.entries(holidays)) {
      merged[key] = merged[key] ? [...new Set([...merged[key], ...days])] : days;
    }
    return merged;
  }, [japanHolidays, holidays]);
  const [currentView, setCurrentView] = useState<0 | 1 | 2>(effectiveStartView);
  const [open, setOpen] = useState(false);
  const defaultEraRawDigits = defaultEra ? DEFAULT_ERA_RAW_DIGITS[defaultEra] : null;
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  /** Raw digit string the user is typing (e.g. "507"); null = not in typing mode */
  const [rawDigits, setRawDigits] = useState<string | null>(null);
  /** When true, the trailing suffix (年/月/日) is hidden — intermediate backspace step */
  const [hideSuffix, setHideSuffix] = useState(false);
  /** Set when user deletes in the middle: free-edit buffer, no validation until blur/Enter */
  const [inputBuffer, setInputBuffer] = useState<string | null>(null);
  /** True when we want popup to highlight a just-typed valid date before parent `value` updates */
  const [pickedPreview, setPickedPreview] = useState(false);
  /** When user clears input, immediately stop highlighting previous picked date until `value` catches up */
  const [clearedPreview, setClearedPreview] = useState(false);
  const composingRef = useRef(false);
  /**
   * Set true by onKeyDown when its era/digit handler successfully consumed
   * a key (and called preventDefault). Checked by the Android onChange fast
   * path so we don't double-process the same key when a platform fires both
   * keydown AND the input event (e.g., some Android Chrome configurations
   * where preventDefault on keydown still allows the input event through).
   * A setTimeout(0) resets the flag so a subsequent independent onChange isn't
   * suppressed (microtask is too eager — fires between keydown and input).
   */
  const justHandledByKeyDownRef = useRef(false);
  const suppressBlurCloseRef = useRef(false);
  /** Next onChange is from Backspace/Delete (switch to free-edit, no validate) */
  const deleteModeRef = useRef(false);
  const maxDigits = getWarekiMaxDigits(picker);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    placement: 'top' | 'bottom';
  }>({ top: 0, left: 0, width: 280, placement: 'bottom' });
  const inputRef = useRef<InputRef>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // RHF Controller (and other refs from outside) get the same antd InputRef the
  // internal logic already uses — avoids the "function components cannot be given refs" warning.
  useImperativeHandle(externalRef, () => inputRef.current as InputRef);

  const startDate = useMemo(() => {
    if (effectiveStartDateProp === null || effectiveStartDateProp === '') return null;
    if (effectiveStartDateProp instanceof Date) return effectiveStartDateProp;
    // Handle Dayjs-like objects
    if (typeof effectiveStartDateProp === 'object' && 'toDate' in effectiveStartDateProp) {
      return (effectiveStartDateProp as any).toDate();
    }
    if (typeof effectiveStartDateProp === 'string') {
      const v = effectiveStartDateProp.trim();
      if (!v || v === 'Invalid Date') return null;
      return parseDate(v, format);
    }
    return null;
  }, [effectiveStartDateProp, format]);

  const endDate = useMemo(() => {
    if (effectiveEndDateProp === null || effectiveEndDateProp === '') return null;
    if (effectiveEndDateProp instanceof Date) return effectiveEndDateProp;
    if (typeof effectiveEndDateProp === 'object' && 'toDate' in effectiveEndDateProp) {
      return (effectiveEndDateProp as any).toDate();
    }
    if (typeof effectiveEndDateProp === 'string') {
      const v = effectiveEndDateProp.trim();
      if (!v || v === 'Invalid Date') return null;
      return parseDate(v, format);
    }
    return null;
  }, [effectiveEndDateProp, format]);

  useEffect(() => {
    const v = value?.trim();
    if (v) {
      const parsed = parseDate(v, format);
      if (!Number.isNaN(parsed.getTime())) {
        setDate(parsed);
        setViewDate(new Date(parsed));
        setPickedPreview(false);
        setClearedPreview(false);
        setInputBuffer(null);
        setRawDigits(null);
        setHideSuffix(false);
      }
    } else {
      setPickedPreview(false);
      setClearedPreview(true);
      setInputBuffer(null);
      setRawDigits(null);
      setHideSuffix(false);
    }
  }, [value, format, defaultEraRawDigits]);

  const updatePosition = useCallback(() => {
    const input = inputRef.current?.nativeElement ?? inputRef.current?.input;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const offsetBelow = 5;
    const offsetAbove = 5;
    const pickerHeight = pickerRef.current?.offsetHeight || 300;
    // Measure against the visual viewport (excludes on-screen keyboard) so
    // spaceBelow reflects what the user actually sees on iOS Safari, where
    // window.innerHeight stays full-height even when the keyboard is up.
    const vv = window.visualViewport;
    const viewportHeight = vv?.height ?? window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const showBelow = popupPlacement === 'bottom' || spaceBelow >= pickerHeight + offsetBelow;
    // Use page-relative coordinates (rect + scrollY) combined with
    // `position: absolute` for every code path. `position: fixed` on iOS Safari
    // is unreliable when the on-screen keyboard is open — the popup ends up
    // anchored to the layout viewport while the visual viewport has shifted,
    // putting the calendar far from the input. Absolute positioning eliminates
    // this mismatch since it follows the document flow directly.
    const top = showBelow
      ? rect.bottom + window.scrollY + offsetBelow
      : rect.top + window.scrollY - pickerHeight - offsetAbove;
    const placement = showBelow ? 'bottom' : 'top';
    const viewportLeft = window.scrollX + (vv?.offsetLeft ?? 0);
    const viewportWidth = vv?.width ?? window.innerWidth;
    const viewportPadding = 8;
    const availableWidth = Math.max(200, viewportWidth - viewportPadding * 2);
    const maxW = 356;
    const width = Math.max(240, Math.round(rect.width));
    const w = Math.min(width, maxW, availableWidth);
    const rawLeft = rect.left + window.scrollX;
    const minLeft = viewportLeft + viewportPadding;
    const maxLeft = viewportLeft + viewportWidth - w - viewportPadding;
    const left = Math.min(Math.max(rawLeft, minLeft), Math.max(minLeft, maxLeft));
    // Update popup position synchronously so it follows scroll in the same frame (no "ghost" lag)
    if (pickerRef.current) {
      const el = pickerRef.current;
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
      el.style.width = `${w}px`;
      el.style.minWidth = `${w}px`;
      el.style.maxWidth = `${maxW}px`;
      const placementClass = placement === 'top' ? 'datepicker-bottom-left' : 'datepicker-top-left';
      el.className = `datepicker-container datepicker-dropdown ${placementClass}`;
    }
    setPosition({ top, left, width, placement });
  }, [popupPlacement]);

  const openPicker = useCallback(() => {
    setCurrentView(effectiveStartView);
    updatePosition();
    setOpen(true);
    requestAnimationFrame(updatePosition);
  }, [effectiveStartView, updatePosition]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(() => updatePosition());
    const onResize = () => updatePosition();
    // rAF so we read layout after scroll is applied; ref update inside updatePosition keeps popup in sync (no ghost)
    const onScroll = () => requestAnimationFrame(updatePosition);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    // visualViewport resize fires on iOS Safari when the on-screen keyboard
    // opens/closes; window 'resize' alone does not, so without this listener
    // the picker keeps its pre-keyboard placement decision (e.g., stays
    // below) even after the keyboard cuts off the space.
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onScroll);
    }
    const inputEl = inputRef.current?.input ?? inputRef.current?.nativeElement;
    const scrollParents: (Window | Element)[] = [window];
    if (inputEl) {
      let el: HTMLElement | null = inputEl.parentElement;
      while (el) {
        const { overflow, overflowY } = getComputedStyle(el);
        if (/(auto|scroll|overlay)/.test(overflow + overflowY)) scrollParents.push(el);
        el = el.parentElement;
      }
    }
    scrollParents.forEach(p => {
      if (p === window) return;
      (p as Element).addEventListener('scroll', onScroll);
    });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onScroll);
      }
      scrollParents.forEach(p => {
        if (p !== window) (p as Element).removeEventListener('scroll', onScroll);
      });
    };
  }, [open, currentView, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onGlobalClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || pickerRef.current?.contains(target)) return;
      setOpen(false);
    };
    let cancelled = false;
    const tid = window.setTimeout(() => {
      if (!cancelled) document.addEventListener('click', onGlobalClick);
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(tid);
      document.removeEventListener('click', onGlobalClick);
    };
  }, [open]);

  const displayValue = useMemo(() => {
    if (!value?.trim()) return '';
    const parsed = parseDate(value, format);
    if (Number.isNaN(parsed.getTime())) return value;
    if (shortFormat) {
      return convGtJDateShort(dayjs(parsed));
    }
    return formatDate(parsed, format);
  }, [value, format, shortFormat]);
  const defaultEraDisplayValue = useMemo(
    () =>
      defaultEraRawDigits
        ? (formatWarekiProgressive(defaultEraRawDigits, picker, hideSuffix) ?? '')
        : '',
    [defaultEraRawDigits, picker, hideSuffix]
  );

  const showView = useCallback(
    (view: 0 | 1 | 2) => {
      setCurrentView(view);
      setTimeout(updatePosition, 0);
    },
    [updatePosition]
  );

  /** Commit selection: use selectedDate if provided (calendar pick), else current date. Always clear rawDigits so input shows displayValue. */
  const pickAndMaybeHide = useCallback(
    (selectedDate?: Date) => {
      const dateToUse = selectedDate ?? date;
      const formatted = formatDate(dateToUse, format);
      onChange?.(formatted);
      onCalendarCommit?.(formatted);
      setInputBuffer(null);
      setRawDigits(null);
      setHideSuffix(false);
      if (autoHide) setOpen(false);
    },
    [date, format, onChange, onCalendarCommit, autoHide]
  );

  const weekStart = JA_JP.weekStart % 7;
  const daysMin = [...JA_JP.daysMin.slice(weekStart), ...JA_JP.daysMin.slice(0, weekStart)];

  const strictParseWarekiShorthand = useCallback(
    (digits: string) => {
      const parsed = parseWarekiShorthand(digits, picker);
      if (!parsed || Number.isNaN(parsed.getTime())) return null;
      // JS Date will normalize overflow (e.g. 3/32 -> 4/1). Reject those.
      const roundtrip = dateToRawDigits(parsed, picker);
      return roundtrip === digits ? parsed : null;
    },
    [picker]
  );

  const isStrictFormattedInput = useCallback(
    (input: string, parsed: Date) => {
      // Reject normalization like 令和08年03月32日 -> 令和08年04月01日
      return formatDate(parsed, format) === input;
    },
    [format]
  );

  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth();
  const thisDay = now.getDate();
  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  /** Only show "picked" when user has actually selected a value (no initial selection) */
  const hasValue = !!value?.trim();
  const shouldShowPicked = (hasValue && !clearedPreview) || pickedPreview;

  const renderYears = () => {
    const items: React.ReactNode[] = [];
    const minRenderYear = YEAR_BOUNDARIES.min;
    const maxRenderYear = YEAR_BOUNDARIES.max;
    const minAllowedYear = startDate
      ? Math.max(minRenderYear, startDate.getFullYear())
      : minRenderYear;
    const maxAllowedYear = endDate ? Math.min(maxRenderYear, endDate.getFullYear()) : maxRenderYear;

    const total = YEARS_RANGE.end - YEARS_RANGE.start + 1; // 20
    let rangeStart = YEARS_RANGE.start;
    let rangeEnd = YEARS_RANGE.end;

    // Shift the window so we never render out-of-bound years (avoids blank cells in 4-col grid).
    const initialStartYear = viewYear + rangeStart;
    if (initialStartYear < minRenderYear) {
      rangeStart += minRenderYear - initialStartYear;
      rangeEnd = rangeStart + (total - 1);
    }
    if (viewYear + rangeEnd > maxRenderYear) {
      rangeEnd -= viewYear + rangeEnd - maxRenderYear;
      rangeStart = rangeEnd - (total - 1);
    }

    const startYear = viewYear + rangeStart;
    const endYear = viewYear + rangeEnd;

    // Disable navigation only when a click would not change the view (same clamp logic as in handleClick).
    let prevDisabled = Math.max(viewYear - 20, minRenderYear) === viewYear;
    let nextDisabled = Math.min(viewYear + 20, maxRenderYear) === viewYear;

    for (let y = startYear; y <= endYear; y += 1) {
      const dateItem = new Date(y, 1, 1);
      const disabled = y < minAllowedYear || y > maxAllowedYear;
      const picked = shouldShowPicked && y === year;
      items.push(
        createItem(
          [
            picked ? 'picked' : '',
            disabled ? 'disabled' : '',
            dateItem.getFullYear() === thisYear ? 'highlighted' : '',
          ],
          disabled ? 'year disabled' : picked ? 'year picked' : 'year',
          convertWareki(y),
          y
        )
      );
    }

    const headerText = `${convertWareki(startYear)}${JA_JP.yearSuffix} - ${convertWareki(endYear)}${JA_JP.yearSuffix}`;
    return (
      <div className="datepicker-panel" data-view="years picker">
        <ul>
          <li data-view="years prev" className={prevDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-left" aria-hidden />
          </li>
          <li data-view="years current" className="disabled">
            {headerText}
          </li>
          <li data-view="years next" className={nextDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-right" aria-hidden />
          </li>
        </ul>
        <ul data-view="years">{items}</ul>
      </div>
    );
  };

  const renderMonths = () => {
    const items: React.ReactNode[] = [];
    let prevDisabled = false;
    let nextDisabled = false;
    // Check year boundary
    prevDisabled = viewYear - 1 < YEAR_BOUNDARIES.min;
    nextDisabled = viewYear + 1 > YEAR_BOUNDARIES.max;
    // Check startDate/endDate constraints
    for (let i = 0; i <= 11; i += 1) {
      let disabled = false;
      if (startDate) {
        if (viewYear < startDate.getFullYear()) {
          disabled = true;
        } else if (viewYear === startDate.getFullYear()) {
          disabled = i < startDate.getMonth();
        }
      }
      if (!disabled && endDate) {
        if (viewYear > endDate.getFullYear()) {
          disabled = true;
        } else if (viewYear === endDate.getFullYear()) {
          disabled = i > endDate.getMonth();
        }
      }
      const picked = shouldShowPicked && viewYear === year && i === month;
      items.push(
        createItem(
          [
            picked ? 'picked' : '',
            disabled ? 'disabled' : '',
            viewYear === thisYear && i === thisMonth ? 'highlighted' : '',
          ],
          disabled ? 'month disabled' : picked ? 'month picked' : 'month',
          JA_JP.monthsShort[i],
          `${viewYear}-${i}`
        )
      );
    }
    const viewdate1 = convertWareki(viewYear) + JA_JP.yearSuffix;
    return (
      <div className="datepicker-panel" data-view="months picker">
        <ul>
          <li data-view="year prev" className={prevDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-left" aria-hidden />
          </li>
          <li data-view="year current" className={prevDisabled && nextDisabled ? 'disabled' : ''}>
            {viewdate1}
          </li>
          <li data-view="year next" className={nextDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-right" aria-hidden />
          </li>
        </ul>
        <ul data-view="months">{items}</ul>
      </div>
    );
  };

  const renderDays = () => {
    const prevViewYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const prevViewMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const lengthPrev = getDaysInMonth(prevViewYear, prevViewMonth);
    const firstDay = new Date(viewYear, viewMonth, 1);
    let n = firstDay.getDay() - weekStart;
    if (n <= 0) n += 7;
    const prevItems: React.ReactNode[] = [];
    for (let i = lengthPrev - (n - 1); i <= lengthPrev; i += 1) {
      const prevViewDate = new Date(prevViewYear, prevViewMonth, i);
      let disabled = startDate ? prevViewDate.getTime() < startDate.getTime() : false;
      const yearMonth = `${prevViewYear}-${String(prevViewMonth + 1).padStart(2, '0')}`;
      const dayStr = String(i).padStart(2, '0');
      const isHoliday = checkHolidayForMonth(mergedHolidays, yearMonth, dayStr);
      prevItems.push(
        createItem(
          [
            'muted',
            disabled ? 'disabled' : '',
            shouldShowPicked && prevViewYear === year && prevViewMonth === month && i === day
              ? 'picked'
              : '',
            prevViewYear === thisYear && prevViewMonth === thisMonth && i === thisDay
              ? 'highlighted'
              : '',
            isHoliday ? 'holiday' : '',
          ],
          'day prev',
          i,
          `${prevViewYear}-${prevViewMonth}-${i}-prev`
        )
      );
    }
    const lengthCur = getDaysInMonth(viewYear, viewMonth);
    const nextViewYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    const nextViewMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const nNext = 42 - (prevItems.length + lengthCur);
    const nextItems: React.ReactNode[] = [];
    for (let i = 1; i <= nNext; i += 1) {
      const nextViewDate = new Date(nextViewYear, nextViewMonth, i);
      let disabled = endDate ? nextViewDate.getTime() > endDate.getTime() : false;
      const yearMonth = `${nextViewYear}-${String(nextViewMonth + 1).padStart(2, '0')}`;
      const dayStr = String(i).padStart(2, '0');
      const isHoliday = checkHolidayForMonth(mergedHolidays, yearMonth, dayStr);
      nextItems.push(
        createItem(
          [
            'muted',
            disabled ? 'disabled' : '',
            shouldShowPicked && nextViewYear === year && nextViewMonth === month && i === day
              ? 'picked'
              : '',
            nextViewYear === thisYear && nextViewMonth === thisMonth && i === thisDay
              ? 'highlighted'
              : '',
            isHoliday ? 'holiday' : '',
          ],
          'day next',
          i,
          `${nextViewYear}-${nextViewMonth}-${i}-next`
        )
      );
    }
    const curItems: React.ReactNode[] = [];
    for (let i = 1; i <= lengthCur; i += 1) {
      const curDate = new Date(viewYear, viewMonth, i);
      let disabled = false;
      if (startDate) disabled = curDate.getTime() < startDate.getTime();
      if (!disabled && endDate) disabled = curDate.getTime() > endDate.getTime();
      const yearMonth = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
      const dayStr = String(i).padStart(2, '0');
      const isHoliday = checkHolidayForMonth(mergedHolidays, yearMonth, dayStr);
      const picked = shouldShowPicked && viewYear === year && viewMonth === month && i === day;
      curItems.push(
        createItem(
          [
            disabled ? 'disabled' : '',
            picked ? 'picked' : '',
            viewYear === thisYear && viewMonth === thisMonth && i === thisDay ? 'highlighted' : '',
            isHoliday ? 'holiday' : '',
          ],
          disabled ? 'day disabled' : picked ? 'day picked' : 'day',
          i,
          `${viewYear}-${viewMonth}-${i}`
        )
      );
    }
    const prevDisabled = viewYear === YEAR_BOUNDARIES.min && viewMonth === 0;
    const nextDisabled = viewYear === YEAR_BOUNDARIES.max && viewMonth === 11;
    const viewdate1 =
      convertWarekiMonth(viewYear, viewMonth + 1) +
      JA_JP.yearSuffix +
      ' ' +
      JA_JP.monthsShort[viewMonth];
    const viewdate2 = JA_JP.monthsShort[viewMonth] + ' ' + viewYear + JA_JP.yearSuffix;
    const monthHeader = JA_JP.yearFirst ? viewdate1 : viewdate2;

    return (
      <div className="datepicker-panel" data-view="days picker">
        <ul>
          <li data-view="month prev" className={prevDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-left" aria-hidden />
          </li>
          <li data-view="month current" className={prevDisabled && nextDisabled ? 'disabled' : ''}>
            {monthHeader}
          </li>
          <li data-view="month next" className={nextDisabled ? 'disabled' : ''}>
            <i className="fa fa-chevron-right" aria-hidden />
          </li>
        </ul>
        <ul data-view="week">{daysMin.map((d, idx) => createItem([], 'week', d, idx))}</ul>
        <ul data-view="days">
          {prevItems}
          {curItems}
          {nextItems}
        </ul>
      </div>
    );
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const li = target.closest('li');
    const view = li?.getAttribute('data-view') ?? target.getAttribute('data-view');
    if (!view || (li ?? target).classList.contains('disabled')) return;
    e.preventDefault();
    e.stopPropagation();

    switch (view) {
      case 'years prev': {
        setViewDate(d => {
          const newYear = d.getFullYear() - 20;
          // Clamp to boundaries
          const clampedYear = Math.max(newYear, YEAR_BOUNDARIES.min);
          if (clampedYear === d.getFullYear()) return d; // No change if at boundary
          return new Date(
            clampedYear,
            d.getMonth(),
            getMinDay(clampedYear, d.getMonth(), d.getDate())
          );
        });
        break;
      }
      case 'years next': {
        setViewDate(d => {
          const newYear = d.getFullYear() + 20;
          // Clamp to boundaries
          const clampedYear = Math.min(newYear, YEAR_BOUNDARIES.max);
          if (clampedYear === d.getFullYear()) return d; // No change if at boundary
          return new Date(
            clampedYear,
            d.getMonth(),
            getMinDay(clampedYear, d.getMonth(), d.getDate())
          );
        });
        break;
      }
      case 'year prev': {
        setViewDate(d => {
          const newYear = d.getFullYear() - 1;
          // Clamp to boundaries
          const clampedYear = Math.max(newYear, YEAR_BOUNDARIES.min);
          if (clampedYear === d.getFullYear()) return d; // No change if at boundary
          return new Date(
            clampedYear,
            d.getMonth(),
            getMinDay(clampedYear, d.getMonth(), d.getDate())
          );
        });
        break;
      }
      case 'year next': {
        setViewDate(d => {
          const newYear = d.getFullYear() + 1;
          // Clamp to boundaries
          const clampedYear = Math.min(newYear, YEAR_BOUNDARIES.max);
          if (clampedYear === d.getFullYear()) return d; // No change if at boundary
          return new Date(
            clampedYear,
            d.getMonth(),
            getMinDay(clampedYear, d.getMonth(), d.getDate())
          );
        });
        break;
      }
      case 'year current':
        if (format.hasYear) showView(VIEWS.YEARS);
        break;
      case 'year picked':
      case 'year': {
        let yearCommitDate: Date | undefined;
        if (view === 'year') {
          const y = convertSeireki(target.textContent || '');
          if (!Number.isNaN(y)) {
            let newDate = new Date(y, 0, 1);
            if (startDate && newDate.getTime() < startDate.getTime()) {
              newDate = new Date(startDate);
            }
            if (endDate && newDate.getTime() > endDate.getTime()) {
              newDate = new Date(endDate);
            }
            setDate(newDate);
            setViewDate(new Date(newDate));
            yearCommitDate = newDate;
            onChange?.(formatDate(newDate, format));
            setInputBuffer(null);
            setRawDigits(null);
            setHideSuffix(false);
          }
        }
        if (picker !== 'year' && format.hasMonth) showView(VIEWS.MONTHS);
        else pickAndMaybeHide(yearCommitDate);
        break;
      }
      case 'month prev': {
        setViewDate(d => {
          let newMonth = d.getMonth() - 1;
          let newYear = d.getFullYear();
          if (newMonth < 0) {
            newMonth = 11;
            newYear -= 1;
          }
          // Clamp year to boundaries
          newYear = Math.max(newYear, YEAR_BOUNDARIES.min);
          return new Date(newYear, newMonth, getMinDay(newYear, newMonth, d.getDate()));
        });
        break;
      }
      case 'month next': {
        setViewDate(d => {
          let newMonth = d.getMonth() + 1;
          let newYear = d.getFullYear();
          if (newMonth > 11) {
            newMonth = 0;
            newYear += 1;
          }
          // Clamp year to boundaries
          newYear = Math.min(newYear, YEAR_BOUNDARIES.max);
          return new Date(newYear, newMonth, getMinDay(newYear, newMonth, d.getDate()));
        });
        break;
      }
      case 'month current':
        if (format.hasMonth) showView(VIEWS.MONTHS);
        break;
      case 'month picked':
      case 'month': {
        let monthCommitDate: Date | undefined;
        if (view === 'month') {
          const idx = JA_JP.monthsShort.indexOf(
            (target?.textContent as
              | '01月'
              | '02月'
              | '03月'
              | '04月'
              | '05月'
              | '06月'
              | '07月'
              | '08月'
              | '09月'
              | '10月'
              | '11月'
              | '12月') || ''
          );
          if (idx !== -1) {
            let newDate = new Date(viewYear, idx, getMinDay(viewYear, idx, date.getDate()));
            // Clamp to startDate/endDate so picking the boundary month jumps inside the valid range.
            if (startDate && newDate.getTime() < startDate.getTime()) {
              newDate = new Date(startDate);
            }
            if (endDate && newDate.getTime() > endDate.getTime()) {
              newDate = new Date(endDate);
            }
            setDate(newDate);
            setViewDate(new Date(newDate));
            monthCommitDate = newDate;
            onChange?.(formatDate(newDate, format));
            setInputBuffer(null);
            setRawDigits(null);
            setHideSuffix(false);
          }
        }
        if (format.hasDay) showView(VIEWS.DAYS);
        else pickAndMaybeHide(monthCommitDate);
        break;
      }
      case 'day prev':
      case 'day next':
      case 'day': {
        const dayNum = parseInt(target.textContent || '0', 10);
        if (view === 'day prev') {
          const prevViewYear = viewMonth === 0 ? viewYear - 1 : viewYear;
          const prevViewMonth = viewMonth === 0 ? 11 : viewMonth - 1;
          const dayDate = new Date(prevViewYear, prevViewMonth, dayNum);
          setViewDate(() => dayDate);
          setDate(dayDate);
          pickAndMaybeHide(dayDate);
        } else if (view === 'day next') {
          const nextViewYear = viewMonth === 11 ? viewYear + 1 : viewYear;
          const nextViewMonth = viewMonth === 11 ? 0 : viewMonth + 1;
          const dayDate = new Date(nextViewYear, nextViewMonth, dayNum);
          setViewDate(() => dayDate);
          setDate(dayDate);
          pickAndMaybeHide(dayDate);
        } else {
          const dayDate = new Date(viewYear, viewMonth, dayNum);
          setDate(dayDate);
          pickAndMaybeHide(dayDate);
        }
        break;
      }
      case 'day picked':
        pickAndMaybeHide();
        break;
      default:
        break;
    }
  };

  const pickerStyle = useMemo(() => {
    const maxW = 356;
    const w = Math.min(position.width, maxW);
    const base: React.CSSProperties = {
      // Absolute (vs fixed) for every mode — `position: fixed` on iOS Safari
      // misbehaves when the on-screen keyboard is open and the visual viewport
      // shifts away from the layout viewport. `top` / `left` are already in
      // page coordinates (rect + scrollY/scrollX) so the popup tracks the
      // input correctly across platforms.
      position: 'absolute',
      top: position.top,
      left: position.left,
      width: w,
      minWidth: w,
      maxWidth: maxW,
      // 1200 > 1100 used by the massager layout's sticky header / footer and
      // the sticky save-bar at the bottom of CheckinRemarksPage / CheckinListPage.
      // At 1050 the picker dropdown was sandwiched: top/bottom rows visible but
      // the middle covered by those sticky bars on Android viewports.
      zIndex: 1000,
    };
    if (colors) {
      (base as Record<string, string>)['--datepicker-primary'] = colors.primary;
      (base as Record<string, string>)['--datepicker-primary-hover'] = colors.primaryHover;
    }
    return base;
  }, [position.top, position.left, position.width, colors]);

  const pickerContent = (
    <div
      ref={pickerRef}
      className={`datepicker-container datepicker-dropdown ${
        popupPlacement === 'bottom'
          ? 'datepicker-top-left'
          : position.placement === 'top'
            ? 'datepicker-bottom-left'
            : 'datepicker-top-left'
      } ${!open ? 'datepicker-hide' : ''}`}
      style={pickerStyle}
      onMouseDownCapture={() => {
        // Clicking inside the portal popup blurs the input first.
        // Prevent the blur handler from closing the popup before the click is processed.
        suppressBlurCloseRef.current = true;
        setTimeout(() => {
          suppressBlurCloseRef.current = false;
        }, 0);
      }}
      onClick={handleClick}
      role="dialog"
      aria-label="日付を選択"
    >
      {currentView === VIEWS.YEARS && renderYears()}
      {currentView === VIEWS.MONTHS && renderMonths()}
      {currentView === VIEWS.DAYS && renderDays()}
    </div>
  );

  const tryParseStrictFromText = useCallback(
    (text: string): Date | null => {
      const trimmed = text.trim();
      if (!trimmed) return null;
      const validWarekiDay = /^(令和|平成|昭和|大正|明治)\d{1,2}年\d{1,2}月\d{1,2}日$/;
      const validWarekiDayNoSuffix = /^(令和|平成|昭和|大正|明治)\d{1,2}年\d{1,2}月\d{1,2}$/;
      const validWarekiMonth = /^(令和|平成|昭和|大正|明治)\d{1,2}年\d{1,2}月$/;
      const validWarekiMonthNoSuffix = /^(令和|平成|昭和|大正|明治)(\d{1,2})年(\d{1,2})$/;
      const validWarekiYear = /^(令和|平成|昭和|大正|明治)\d{1,2}年?$/;
      const looksValidWareki =
        picker === 'year'
          ? validWarekiYear.test(trimmed)
          : picker === 'month'
            ? validWarekiMonth.test(trimmed) || validWarekiMonthNoSuffix.test(trimmed)
            : validWarekiDay.test(trimmed) || validWarekiDayNoSuffix.test(trimmed);
      const digitsOnly = /^\d+$/.test(trimmed.replace(/\s/g, ''));
      if (looksValidWareki) {
        let augmented = trimmed;
        if (picker === 'year' && !trimmed.endsWith('年')) {
          augmented = `${trimmed}年`;
        } else if (picker === 'month' && validWarekiMonthNoSuffix.test(trimmed)) {
          const mm = trimmed.match(validWarekiMonthNoSuffix);
          if (mm) {
            const monthNum = parseInt(mm[3], 10);
            if (monthNum < 1 || monthNum > 12) return null;
          }
          augmented = `${trimmed}月`;
        } else if (picker === 'day' && validWarekiDayNoSuffix.test(trimmed)) {
          augmented = `${trimmed}日`;
        }
        const parsed = parseDate(augmented, format);
        if (Number.isNaN(parsed.getTime())) return null;
        return isStrictFormattedInput(augmented, parsed) ? parsed : null;
      }
      if (digitsOnly) {
        return strictParseWarekiShorthand(trimmed.replace(/\s/g, ''));
      }
      return null;
    },
    [picker, format, isStrictFormattedInput, strictParseWarekiShorthand]
  );

  const tryNormalizePreviewInput = useCallback(
    (text: string): { normalizedText: string; previewDate: Date } | null => {
      if (picker !== 'day' && picker !== 'month') return null;
      const trimmed = text.trim();
      if (!trimmed) return null;

      const yearOnlyNoSuffix = /^(令和|平成|昭和|大正|明治)\d{2}$/;
      const yearOnly = /^(令和|平成|昭和|大正|明治)\d{1,2}年$/;
      const monthOnly = /^(令和|平成|昭和|大正|明治)\d{1,2}年\d{1,2}月$/;
      const yearMonthNoSuffix = /^(令和|平成|昭和|大正|明治)(\d{1,2})年(\d{1,2})$/;

      // For month picker: 令和08 or 令和08年 → preview with month 01
      if (picker === 'month' && (yearOnly.test(trimmed) || yearOnlyNoSuffix.test(trimmed))) {
        const ym = trimmed.match(/^(令和|平成|昭和|大正|明治)(\d{1,2})/);
        if (ym && parseInt(ym[2], 10) === 0) return null;
        const normalizedText = yearOnlyNoSuffix.test(trimmed) ? `${trimmed}年` : trimmed;
        const dayFormat = parseFormat(JA_JP.format);
        const augmentedFull = `${normalizedText}01月01日`;
        const parsed = parseDate(augmentedFull, dayFormat);
        if (Number.isNaN(parsed.getTime())) return null;
        if (startDate && parsed.getTime() < startDate.getTime()) return null;
        if (endDate && parsed.getTime() > endDate.getTime()) return null;
        return { normalizedText, previewDate: parsed };
      }

      // For month picker: 令和08年02 → normalize to 令和08年02月
      // Only auto-append 月 when month is unambiguous: 2-9 (single digit) or 2 digits (01-12)
      if (picker === 'month') {
        const mm = trimmed.match(yearMonthNoSuffix);
        if (mm) {
          const monthStr = mm[3];
          const monthNum = parseInt(monthStr, 10);
          if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) return null;
          // Single digit 1 could be start of 10,11,12 — don't auto-append 月
          if (monthStr.length === 1 && monthNum === 1) return null;
          const normalizedText = `${trimmed}月`;
          const dayFormat = parseFormat(JA_JP.format);
          const augmentedFull = `${normalizedText}01日`;
          const parsed = parseDate(augmentedFull, dayFormat);
          if (Number.isNaN(parsed.getTime())) return null;
          if (startDate && parsed.getTime() < startDate.getTime()) return null;
          if (endDate && parsed.getTime() > endDate.getTime()) return null;
          return { normalizedText, previewDate: parsed };
        }
        return null;
      }

      // Day picker logic below
      if (yearOnly.test(trimmed) || yearOnlyNoSuffix.test(trimmed)) {
        const yd = trimmed.match(/^(令和|平成|昭和|大正|明治)(\d{1,2})/);
        if (yd && parseInt(yd[2], 10) === 0) return null;
        const normalizedText = yearOnlyNoSuffix.test(trimmed) ? `${trimmed}年` : trimmed;
        const augmented = `${normalizedText}01月01日`;
        const parsed = parseDate(augmented, format);
        if (Number.isNaN(parsed.getTime())) return null;
        if (!isStrictFormattedInput(augmented, parsed)) return null;
        if (startDate && parsed.getTime() < startDate.getTime()) return null;
        if (endDate && parsed.getTime() > endDate.getTime()) return null;
        return { normalizedText, previewDate: parsed };
      }

      if (monthOnly.test(trimmed)) {
        const augmented = `${trimmed}01日`;
        const parsed = parseDate(augmented, format);
        if (Number.isNaN(parsed.getTime())) return null;
        if (!isStrictFormattedInput(augmented, parsed)) return null;
        if (startDate && parsed.getTime() < startDate.getTime()) return null;
        if (endDate && parsed.getTime() > endDate.getTime()) return null;
        return { normalizedText: trimmed, previewDate: parsed };
      }

      const m = trimmed.match(yearMonthNoSuffix);
      if (m) {
        const monthStr = m[3];
        const monthNum = parseInt(monthStr, 10);
        if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) return null;
        if (monthStr.length === 1 && monthNum === 1) return null;
        const normalizedText = `${trimmed}月`;
        const augmented = `${normalizedText}01日`;
        const parsed = parseDate(augmented, format);
        if (Number.isNaN(parsed.getTime())) return null;
        if (!isStrictFormattedInput(augmented, parsed)) return null;
        return { normalizedText, previewDate: parsed };
      }

      return null;
    },
    [picker, format, isStrictFormattedInput, startDate, endDate]
  );

  const syncPreviewDateIfValid = useCallback(
    (candidate: Date) => {
      if (startDate && candidate.getTime() < startDate.getTime()) return;
      if (endDate && candidate.getTime() > endDate.getTime()) return;
      setDate(candidate);
      setViewDate(new Date(candidate));
      setCurrentView(effectiveStartView);
      setPickedPreview(true);
      setClearedPreview(false);
      // Mirror antd: typing a valid full date updates selection immediately.
      onChange?.(formatDate(candidate, format));
    },
    [startDate, endDate, effectiveStartView, onChange, format]
  );

  /** Commit inputBuffer (set after user deletes in middle). Parse on blur/Enter; invalid or out of range → clear (never fall back to today). */
  const commitInputBuffer = useCallback(() => {
    const raw = inputBuffer ?? '';
    setInputBuffer(null);
    setRawDigits(null);
    setHideSuffix(false);
    const trimmed = raw.trim();
    if (!trimmed) {
      if (value?.trim()) onChange?.('');
      return;
    }
    const parsed = tryParseStrictFromText(trimmed);
    if (!parsed) {
      setPickedPreview(false);
      setClearedPreview(true);
      onChange?.('');
      return;
    }
    if (startDate && parsed.getTime() < startDate.getTime()) {
      setPickedPreview(false);
      setClearedPreview(true);
      onChange?.('');
      return;
    }
    if (endDate && parsed.getTime() > endDate.getTime()) {
      setPickedPreview(false);
      setClearedPreview(true);
      onChange?.('');
      return;
    }
    setDate(parsed);
    setViewDate(new Date(parsed));
    setPickedPreview(true);
    setClearedPreview(false);
    onChange?.(formatDate(parsed, format));
  }, [inputBuffer, tryParseStrictFromText, startDate, endDate, onChange, format, value]);

  /**
   * Append inserted characters to rawDigits — mirror of the onKeyDown digit/era
   * pipeline (auto-pad month/day, validate boundaries, commit when maxDigits reached).
   * Used by the Android onChange path where keydown can't carry character info.
   * Returns true if at least one character was consumed (even if rejected by
   * validation) so the caller can suppress the native input value.
   */
  const appendCharsToRawDigits = useCallback(
    (
      rawChars: string,
      opts?: { startFresh?: boolean; initialDigits?: string }
    ): boolean => {
      if (!rawChars) return false;
      const inputEl = (inputRef.current?.input ?? inputRef.current?.nativeElement) as
        | HTMLInputElement
        | undefined;
      const hasFullSelection = Boolean(
        inputEl &&
          'selectionStart' in inputEl &&
          inputEl.selectionStart !== inputEl.selectionEnd &&
          inputEl.selectionStart === 0 &&
          inputEl.selectionEnd === inputEl.value.length
      );

      let current: string;
      if (opts?.initialDigits !== undefined) {
        // Caller supplied a starting buffer (e.g. parsed from inputBuffer
        // partial-wareki text) — use it verbatim, bypass locked-committed check.
        current = opts.initialDigits;
      } else if (opts?.startFresh) {
        // Caller wants a clean slate (full-selection replace, etc.).
        current = '';
      } else {
        const isLockedCommitted = rawDigits === null && !!value?.trim();
        if (isLockedCommitted && !hasFullSelection) return false;
        current = (
          (isLockedCommitted || hasFullSelection
            ? ''
            : rawDigits ?? (!value?.trim() ? defaultEraRawDigits : null)) ?? ''
        ).replace(/[^0-9]/g, '');
      }
      let consumedAny = false;
      let committedDate: Date | null = null;

      for (const ch of rawChars) {
        const digit = ANDROID_CHAR_TO_DIGIT[ch];
        if (digit == null) continue;
        if (current.length >= maxDigits) {
          consumedAny = true;
          break;
        }
        if (!canAppendWarekiDigit(current, digit, picker)) {
          consumedAny = true;
          continue;
        }
        let next = current + digit;
        if (next.length === 4 && picker !== 'year') {
          const monthFirstDigit = parseInt(next[3], 10);
          if (monthFirstDigit >= 2 && monthFirstDigit <= 9 && isMonthValid(monthFirstDigit)) {
            next = next.slice(0, 3) + '0' + next[3];
          }
        }
        if (next.length === 6 && picker === 'day') {
          const dayFirstDigit = parseInt(next[5], 10);
          if (dayFirstDigit >= 4 && dayFirstDigit <= 9 && isDayValid(dayFirstDigit)) {
            next = next.slice(0, 5) + '0' + next[5];
          }
        }
        if (next.length > maxDigits) break;
        current = next;
        consumedAny = true;
        if (current.length === maxDigits) {
          const parsed = strictParseWarekiShorthand(current);
          if (parsed) committedDate = parsed;
          break;
        }
      }

      if (!consumedAny) return false;

      setHideSuffix(false);
      if (committedDate) {
        setDate(committedDate);
        setViewDate(new Date(committedDate));
        setCurrentView(effectiveStartView);
        onChange?.(formatDate(committedDate, format));
        setRawDigits(null);
        openPicker();
      } else {
        setRawDigits(current);
      }
      return true;
    },
    [
      rawDigits,
      value,
      defaultEraRawDigits,
      maxDigits,
      picker,
      strictParseWarekiShorthand,
      effectiveStartView,
      format,
      onChange,
      openPicker,
    ]
  );

  const commitRawInput = () => {
    if (inputBuffer !== null) {
      commitInputBuffer();
      return;
    }
    if (rawDigits !== null && rawDigits.length > 0) {
      const parsed = strictParseWarekiShorthand(rawDigits);
      if (parsed) {
        setDate(parsed);
        setViewDate(new Date(parsed));
        setCurrentView(effectiveStartView);
        setPickedPreview(true);
        setClearedPreview(false);
        const formatted = formatDate(parsed, format);
        onChange?.(formatted);
      } else if (rawDigits === defaultEraRawDigits && !value?.trim()) {
        setPickedPreview(false);
        setClearedPreview(true);
        setHideSuffix(false);
        return;
      } else {
        setPickedPreview(false);
        setClearedPreview(true);
        onChange?.('');
      }
    } else if (rawDigits !== null && rawDigits.length === 0) {
      if (value?.trim()) {
        setPickedPreview(false);
        setClearedPreview(true);
        onChange?.('');
      }
      setRawDigits('');
      setHideSuffix(false);
      return;
    }
    setRawDigits(null);
    setHideSuffix(false);
  };

  return (
    <div ref={triggerRef} className={className}>
      <AppTextField
        ref={inputRef}
        status={error ? 'error' : undefined}
        value={
          !value?.trim() && inputBuffer === null && rawDigits === null
            ? defaultEraDisplayValue
            : open && inputBuffer !== null
              ? inputBuffer
              : rawDigits !== null
                ? (formatWarekiProgressive(rawDigits, picker, hideSuffix) ?? rawDigits)
                : displayValue
        }
        placeholder={
          placeholder ??
          (picker === 'year'
            ? '元号00年'
            : picker === 'month'
              ? '元号00年00月'
                : '元号00年00月00日')
        }
        inputMode="numeric"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        readOnly={readOnlyInput}
        style={HALF_WIDTH_ALPHANUMERIC_STYLE}
        suffix={
          <span
            role="button"
            tabIndex={-1}
            className={disabled ? '' : 'cursor-pointer'}
            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
              e.preventDefault();
              if (!disabled) {
                openPicker();
                // Always move cursor to the end when opening via calendar icon
                setTimeout(() => {
                  const inputEl = inputRef.current?.input ?? inputRef.current?.nativeElement;
                  if (inputEl && 'value' in inputEl) {
                    const el = inputEl as HTMLInputElement;
                    const len = el.value.length;
                    el.focus();
                    el.setSelectionRange(len, len);
                  }
                }, 0);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
            }}
            aria-hidden
          >
            <i className="opacity-30 fa fa-calendar-o"></i>
          </span>
        }
        disabled={disabled}
        onPaste={e => {
          if (readOnlyInput) {
            e.preventDefault();
            return;
          }
          const raw = e.clipboardData?.getData('text/plain') ?? '';
          const normalized = normalizePastedWareki(raw.trim());
          if (!normalized) return;

          // Full date paste — let onChange handle it normally
          const fullWarekiDay = /^(明治|大正|昭和|平成|令和)\d{1,2}年\d{1,2}月\d{1,2}日$/;
          const fullWarekiMonth = /^(明治|大正|昭和|平成|令和)\d{1,2}年\d{1,2}月$/;
          const fullWarekiYear = /^(明治|大正|昭和|平成|令和)\d{1,2}年?$/;
          const isFull =
            picker === 'year'
              ? fullWarekiYear.test(normalized)
              : picker === 'month'
                ? fullWarekiMonth.test(normalized)
                : fullWarekiDay.test(normalized);
          const isDigitsOnly = /^\d+$/.test(normalized.replace(/\s/g, ''));
          if (isFull || isDigitsOnly) return; // let onChange handle complete input

          // Partial wareki paste — intercept and enter inputBuffer mode
          if (PARTIAL_WAREKI_RE.test(normalized)) {
            e.preventDefault();
            setRawDigits(null);
            setHideSuffix(false);
            setInputBuffer(normalized);
            if (!open) {
              openPicker();
            }
            const preview = tryNormalizePreviewInput(normalized);
            if (preview) {
              setDate(preview.previewDate);
              setViewDate(new Date(preview.previewDate));
              setInputBuffer(preview.normalizedText);
            }
            return;
          }

          // Era letter only (e.g. pasted "r" → normalized to "令和") — already handled above by PARTIAL_WAREKI_RE
          // Anything else — let onChange handle/reject it
        }}
        // Allow paste/cut/IME commit via onChange, but keep normal typing handled by onKeyDown.
        onChange={e => {
          if (readOnlyInput) return;
          const isComposing = (e.nativeEvent as InputEvent | undefined)?.isComposing;
          const nativeInputEvent = e.nativeEvent as InputEvent | undefined;

          // Android virtual-keyboard fast path — runs BEFORE the isComposing
          // early-return because Gboard fires composition events even for plain
          // Latin/digit input (auto-suggest/auto-correct), which would otherwise
          // trap every keystroke in the composing return below. Android Chrome
          // also fires keydown with key='Unidentified'/keyCode=229 and empty
          // e.code for BOTH character keys and (on some keyboards) Backspace,
          // so the onKeyDown digit/era/backspace pipeline never runs either.
          // The `hasMappable` filter keeps real IME kanji input from being
          // mishandled — only insertions containing a digit (0-9 / ０-９) or a
          // Latin era shortcut (m/M/t/T/s/S/h/H/r/R) are routed through here.
          const nativeInputType = nativeInputEvent?.inputType;
          const isAndroidInsertion =
            nativeInputType === 'insertText' ||
            nativeInputType === 'insertCompositionText' ||
            nativeInputType === 'insertReplacementText';
          const isAndroidDeletion =
            typeof nativeInputType === 'string' && nativeInputType.startsWith('delete');

          if (isAndroidInsertion) {
            // Skip if onKeyDown's era/digit handler already consumed this key
            // (some platforms fire the input event despite keydown.preventDefault).
            // The keydown path already updated rawDigits; running the Android
            // branch too would append the same digit a second time (e.g. type
            // 's' → era handler sets '3' → onChange also sees 's' → '33' →
            // displays '昭和3' instead of '昭和').
            if (justHandledByKeyDownRef.current) {
              justHandledByKeyDownRef.current = false;
              return;
            }
            const newDisplay = e.target.value;
            const expectedDisplay =
              inputBuffer !== null
                ? inputBuffer
                : rawDigits !== null
                  ? (formatWarekiProgressive(rawDigits, picker, hideSuffix) ?? rawDigits ?? '')
                  : (displayValue ?? '');
            if (newDisplay.length > expectedDisplay.length) {
              let i = 0;
              while (
                i < expectedDisplay.length &&
                i < newDisplay.length &&
                expectedDisplay[i] === newDisplay[i]
              ) {
                i += 1;
              }
              const insertedLen = newDisplay.length - expectedDisplay.length;
              const inserted = newDisplay.slice(i, i + insertedLen);
              const hasMappable = [...inserted].some(ch => ANDROID_CHAR_TO_DIGIT[ch] != null);
              if (hasMappable) {
                const inputEl = inputRef.current?.input as HTMLInputElement | undefined;
                const hasFullSelection = Boolean(
                  inputEl &&
                    'selectionStart' in inputEl &&
                    inputEl.selectionStart !== inputEl.selectionEnd &&
                    inputEl.selectionStart === 0 &&
                    inputEl.selectionEnd === inputEl.value.length
                );
                const wasInBuffer = inputBuffer !== null;
                // Truly locked = committed value with NO edit in progress.
                // When inputBuffer is set, user has already started editing
                // (e.g. Backspaced from a committed date) — those keystrokes
                // must be allowed through so the user can refine the partial.
                const wasLocked =
                  !wasInBuffer && rawDigits === null && !!value?.trim();
                if (wasLocked && !hasFullSelection) {
                  // Mirror desktop onKeyDown: a fully-committed value blocks
                  // further typing unless the user replaces the full selection.
                  // Revert DOM imperatively since React won't re-render.
                  if (inputEl && inputEl.value !== expectedDisplay) {
                    inputEl.value = expectedDisplay;
                    try {
                      inputEl.setSelectionRange(
                        expectedDisplay.length,
                        expectedDisplay.length
                      );
                    } catch {
                      /* ignore */
                    }
                  }
                  return;
                }
                // If user was mid-edit in inputBuffer free-edit mode, try to
                // parse the buffer back into the EYYMMDD raw-digit form so we
                // can resume progressive conversion. If it parses, transition
                // out of inputBuffer and append. If it doesn't (text isn't a
                // valid partial wareki), fall through and let the legacy
                // inputBuffer onChange branch append the char as raw text.
                const bufferAsDigits = wasInBuffer
                  ? partialWarekiToRawDigits(inputBuffer ?? '')
                  : null;
                if (!wasInBuffer || bufferAsDigits !== null) {
                  if (wasInBuffer) setInputBuffer(null);
                  deleteModeRef.current = false;
                  composingRef.current = false;
                  const applied = appendCharsToRawDigits(inserted, {
                    startFresh: !wasInBuffer && hasFullSelection,
                    initialDigits: bufferAsDigits?.digits,
                  });
                  if (applied) return;
                }
              }
              // No mappable digit/era chars (e.g. true IME kanji) → fall through
              // to legacy paste / inputBuffer branches below.
            }
          } else if (isAndroidDeletion && !deleteModeRef.current) {
            // Android virtual-keyboard Backspace path. On desktop and on Android
            // keyboards that fire a real key='Backspace', onKeyDown sets
            // deleteModeRef=true and the legacy deleteMode onChange branch
            // handles it — we must NOT run this branch in that case, otherwise
            // we'd double-process the deletion. We only reach here when Android
            // sent Backspace as key='Unidentified'/keyCode=229 (which our
            // onKeyDown guard skipped), leaving deleteModeRef false. Mirror the
            // onKeyDown Backspace logic on rawDigits so progressive wareki
            // rendering survives and we never get stuck in inputBuffer mode.
            const newDisplay = e.target.value;
            if (newDisplay.length === 0) {
              setRawDigits('');
              setHideSuffix(false);
              setInputBuffer(null);
              deleteModeRef.current = false;
              setPickedPreview(false);
              setClearedPreview(true);
              if (value?.trim()) onChange?.('');
              return;
            }
            // Mid-edit (inputBuffer set): try to convert remaining text back
            // into rawDigits so subsequent typing resumes progressive convert.
            // Otherwise just truncate the buffer text.
            if (inputBuffer !== null) {
              const parsedBuf = partialWarekiToRawDigits(newDisplay);
              if (parsedBuf) {
                setInputBuffer(null);
                setRawDigits(parsedBuf.digits);
                setHideSuffix(parsedBuf.hideSuffix);
              } else {
                setInputBuffer(newDisplay);
              }
              return;
            }
            if (rawDigits !== null) {
              if (rawDigits.length === 0) return;
              if (isAtSuffixBoundary(rawDigits.length, picker) && !hideSuffix) {
                setHideSuffix(true);
              } else {
                setRawDigits(rawDigits.slice(0, -1));
                setHideSuffix(false);
              }
              setInputBuffer(null);
              return;
            }
            // rawDigits === null but value committed → expand from committed.
            if (value?.trim()) {
              const existing = dateToRawDigits(date, picker);
              if (existing) {
                if (isAtSuffixBoundary(existing.length, picker) && !hideSuffix) {
                  setRawDigits(existing);
                  setHideSuffix(true);
                } else {
                  setRawDigits(existing.slice(0, -1));
                  setHideSuffix(false);
                }
                setInputBuffer(null);
                return;
              }
            }
            // Nothing to delete → revert DOM if it drifted.
            return;
          }

          // Legacy composing early-return — now AFTER the Android fast path so
          // composition events from Gboard (which fire even for plain Latin
          // input) don't trap mappable era/digit keystrokes here.
          if (isComposing) {
            composingRef.current = true;
            return;
          }
          composingRef.current = false;

          if (open && deleteModeRef.current) {
            deleteModeRef.current = false;
            const nextValue = e.target.value;
            // Strip invisible/non-printable characters that IME may leave behind
            const dvStripped = nextValue.replace(/[^\S ]/g, ' ').trim();
            if (INCOMPLETE_ERA_PREFIX.test(dvStripped)) {
              setInputBuffer(null);
              setRawDigits('');
              setHideSuffix(false);
              setPickedPreview(false);
              setClearedPreview(true);
              onChange?.('');
              return;
            }
            // Select-all + delete while popup is open uses deleteMode; leaving inputBuffer as ''
            // would block onKeyDown digit handling (inputBuffer !== null). Clear buffer like empty field.
            if (!dvStripped) {
              setInputBuffer(null);
              setRawDigits('');
              setHideSuffix(false);
              setPickedPreview(false);
              setClearedPreview(true);
              onChange?.('');
              return;
            }
            setInputBuffer(dvStripped);
            return;
          }
          if (open && inputBuffer !== null) {
            let next = normalizePastedWareki(e.target.value);
            // Strip invisible/non-printable characters that IME may leave behind
            const stripped = next.replace(/[^\S ]/g, ' ').trim();
            // If user cleared the field, exit free-edit mode and resume normal validation immediately.
            if (!stripped) {
              setInputBuffer(null);
              setRawDigits('');
              setHideSuffix(false);
              setPickedPreview(false);
              setClearedPreview(true);
              onChange?.('');
              return;
            }
            next = stripped;
            // If deleting leaves only a single era-prefix character (明|大|昭|平|令), clear all.
            if (INCOMPLETE_ERA_PREFIX.test(next)) {
              setInputBuffer(null);
              setRawDigits('');
              setHideSuffix(false);
              setPickedPreview(false);
              setClearedPreview(true);
              onChange?.('');
              return;
            }
            // Block invalid characters: only allow era names, digits, 年月日.
            // If no valid date characters remain, clear inputBuffer so user can resume normal typing.
            if (!/^(明治|大正|昭和|平成|令和|明|大|昭|平|令)?[\d年月日]*$/.test(next)) {
              const hasDateChars =
                /[\d年月日]/.test(next) || /(明治|大正|昭和|平成|令和|明|大|昭|平|令)/.test(next);
              if (!hasDateChars) {
                setInputBuffer(null);
                setRawDigits('');
                setHideSuffix(false);
                setPickedPreview(false);
                setClearedPreview(true);
                onChange?.('');
              }
              return;
            }
            // Prevent obviously invalid wareki year like 令和888... while in free-edit:
            // allow at most 2 digits after era name when only year is present.
            const eraYearOnlyMatch = next.match(/^(明治|大正|昭和|平成|令和)(\d+)$/);
            if (eraYearOnlyMatch) {
              const eraLabel = eraYearOnlyMatch[1];
              const yearDigits = eraYearOnlyMatch[2];
              if (yearDigits.length > 2) {
                return;
              }
              if (yearDigits.length === 2) {
                const normalizedYear = `${eraLabel}${yearDigits}年`;
                const augmented =
                  picker === 'year'
                    ? normalizedYear
                    : picker === 'month'
                      ? `${normalizedYear}01月`
                      : `${normalizedYear}01月01日`;
                const parsed = parseDate(augmented, format);
                if (Number.isNaN(parsed.getTime()) || !isStrictFormattedInput(augmented, parsed)) {
                  return;
                }
                if (startDate && parsed.getTime() < startDate.getTime()) return;
                if (endDate && parsed.getTime() > endDate.getTime()) return;
              }
            }
            // Prevent invalid month like 令和08年3333: at most 2 digits, value 1–12.
            // Auto-pad month: single digit 2-9 → 02-09
            const eraYearMonthDigitsMatch = next.match(
              /^(明治|大正|昭和|平成|令和)\d{1,2}年(\d+)$/
            );
            if (eraYearMonthDigitsMatch) {
              const monthDigits = eraYearMonthDigitsMatch[2];
              if (monthDigits.length > 2) {
                return;
              }
              if (monthDigits.length === 1) {
                const monthNum = parseInt(monthDigits, 10);
                if (monthNum >= 2 && monthNum <= 9) {
                  next = next.replace(/(\d+)$/, '0' + monthDigits + '月');
                }
              }
              if (monthDigits.length === 2) {
                const monthNum = parseInt(monthDigits, 10);
                if (monthNum < 1 || monthNum > 12) return;
              }
            }
            // Prevent invalid day like 令和08年03月333: at most 2 digits, value 1–31.
            // Auto-pad day: single digit 4-9 → 04-09
            // Month/year picker: block day input entirely
            if (picker !== 'day') {
              // For month picker: block any input after complete month (令和08年02月)
              const hasCompleteMonth = /^(明治|大正|昭和|平成|令和)\d{1,2}年\d{1,2}月/.test(next);
              if (hasCompleteMonth && /月.+$/.test(next)) {
                return;
              }
            } else {
              const eraYearMonthDayDigitsMatch = next.match(
                /^(明治|大正|昭和|平成|令和)\d{1,2}年\d{1,2}月(\d+)$/
              );
              if (eraYearMonthDayDigitsMatch) {
                const dayDigits = eraYearMonthDayDigitsMatch[2];
                if (dayDigits.length > 2) {
                  return;
                }
                if (dayDigits.length === 1) {
                  const dayNum = parseInt(dayDigits, 10);
                  if (dayNum >= 4 && dayNum <= 9) {
                    next = next.replace(/(\d+)$/, '0' + dayDigits + '日');
                  }
                }
                if (dayDigits.length === 2) {
                  const dayNum = parseInt(dayDigits, 10);
                  if (dayNum < 1 || dayNum > 31) return;
                }
              }
            }
            setInputBuffer(next);
            const parsedLive = tryParseStrictFromText(next);
            if (parsedLive) {
              const inRange =
                (!startDate || parsedLive.getTime() >= startDate.getTime()) &&
                (!endDate || parsedLive.getTime() <= endDate.getTime());
              if (inRange) {
                syncPreviewDateIfValid(parsedLive);
                setInputBuffer(null);
                setRawDigits(null);
                setHideSuffix(false);
              }
            } else {
              const preview = tryNormalizePreviewInput(next);
              if (preview) {
                const isTypingNotDeleting = next.length >= (inputBuffer?.length ?? 0);
                if (preview.normalizedText !== next && isTypingNotDeleting)
                  setInputBuffer(preview.normalizedText);
              }
            }
            return;
          }
          const nextValue = e.target.value;
          // Normalize pasted text: fullwidth digits→halfwidth, era letter shortcuts→era name
          const trimmed = normalizePastedWareki(nextValue.trim());
          if (!trimmed) {
            setRawDigits('');
            setHideSuffix(false);
            setPickedPreview(false);
            setClearedPreview(true);
            onChange?.('');
            return;
          }
          // Only accept paste that looks like a date (digits and/or wareki era name).
          // Prevent parseDate from interpreting arbitrary text (e.g. "asdasd") as "today".
          const hasDigits = /\d/.test(trimmed);
          const hasWarekiEra = /(明治|大正|昭和|平成|令和)/.test(trimmed);
          if (!hasDigits && !hasWarekiEra) {
            setRawDigits(null);
            setHideSuffix(false);
            return;
          }
          // Reject obvious invalid wareki strings like 明治23年12月32日 (day > 31 or month > 12)
          const dayMatch = trimmed.match(/(\d{1,2})日/);
          if (dayMatch) {
            const dNum = parseInt(dayMatch[1], 10);
            if (Number.isNaN(dNum) || dNum < 1 || dNum > 31) {
              return;
            }
          }
          const monthMatch = trimmed.match(/(\d{1,2})月/);
          if (monthMatch) {
            const mNum = parseInt(monthMatch[1], 10);
            if (Number.isNaN(mNum) || mNum < 1 || mNum > 12) {
              return;
            }
          }
          // Try to parse full string (supports seireki + wareki like 明治23年12月31日)
          const parsed = parseDate(trimmed, format);
          if (!Number.isNaN(parsed.getTime())) {
            if (!isStrictFormattedInput(trimmed, parsed)) {
              setRawDigits(null);
              setHideSuffix(false);
              onChange?.('');
              return;
            }
            // Enforce startDate/endDate range on paste as well
            if (startDate && parsed.getTime() < startDate.getTime()) {
              setRawDigits(null);
              setHideSuffix(false);
              return;
            }
            if (endDate && parsed.getTime() > endDate.getTime()) {
              setRawDigits(null);
              setHideSuffix(false);
              return;
            }
            const dateFound = parseWarekiShorthand(trimmed, picker);
            if (dateFound) {
              const iso = formatDate(dateFound, format);
              onChange?.(iso);
              setRawDigits(null);
              setHideSuffix(false);
              setInputBuffer(null);
              return;
            }
            setDate(parsed);
            setViewDate(new Date(parsed));
            const formatted = formatDate(parsed, format);
            setRawDigits(null);
            setHideSuffix(false);
            onChange?.(formatted);
            return;
          }
          // Fallback: try shorthand digits (EYYMMDD / YYYYMMDD)
          const digits = (trimmed.match(/\d+/g) ?? []).join('');
          if (!digits) {
            // Partial wareki with no digits yet (e.g. "令和") → enter free-edit mode
            if (PARTIAL_WAREKI_RE.test(trimmed)) {
              setRawDigits(null);
              setHideSuffix(false);
              setInputBuffer(trimmed);
              if (!open) {
                openPicker();
              }
              return;
            }
            // Completely invalid (no digits) → ignore paste and restore previous controlled value
            setRawDigits(null);
            setHideSuffix(false);
            return;
          }
          const parsedShort = strictParseWarekiShorthand(digits);
          if (parsedShort) {
            // Enforce startDate/endDate range
            if (startDate && parsedShort.getTime() < startDate.getTime()) {
              setRawDigits(null);
              setHideSuffix(false);
              return;
            }
            if (endDate && parsedShort.getTime() > endDate.getTime()) {
              setRawDigits(null);
              setHideSuffix(false);
              return;
            }
            setDate(parsedShort);
            setViewDate(new Date(parsedShort));
            const formatted = formatDate(parsedShort, format);
            setRawDigits(null);
            setHideSuffix(false);
            onChange?.(formatted);
            return;
          }
          // Partial wareki with digits (e.g. "令和08年", "令和08年04月") → enter free-edit mode
          if (PARTIAL_WAREKI_RE.test(trimmed)) {
            setRawDigits(null);
            setHideSuffix(false);
            setInputBuffer(trimmed);
            if (!open) {
              openPicker();
            }
            // Try to update calendar preview for the partial date
            const preview = tryNormalizePreviewInput(trimmed);
            if (preview) {
              syncPreviewDateIfValid(preview.previewDate);
              setInputBuffer(preview.normalizedText);
            }
            return;
          }
          // Digits present but cannot form a valid in-range date → ignore and restore previous value
          setRawDigits(null);
          setHideSuffix(false);
        }}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={() => {
          composingRef.current = false;
        }}
        onFocus={() => {
          if (!disabled) {
            openPicker();
            // Restore raw digits from current date so user can continue editing
            if (value?.trim()) {
              const existing = dateToRawDigits(date, picker);
              setRawDigits(existing || '');
              setHideSuffix(false);
            } else {
              const today = new Date();
              setDate(today);
              setViewDate(new Date(today));
              setRawDigits(null);
              setHideSuffix(false);
            }
          }
        }}
        onClick={() => {
          if (!disabled) {
            openPicker();
          }
        }}
        onBlur={() => {
          commitRawInput();
          if (!suppressBlurCloseRef.current) setOpen(false);
        }}
        onKeyDown={e => {
          if (readOnlyInput) {
            if (e.key === 'Enter') {
              e.preventDefault();
              onEnter?.();
              return;
            }
            if (e.key !== 'Tab' && e.key !== 'Escape') {
              e.preventDefault();
            }
            return;
          }
          if (e.ctrlKey || e.metaKey) return;
          // Android virtual keyboard fires keydown with key='Unidentified',
          // keyCode=229 and empty e.code — there's no character info to consume,
          // so skip the desktop/iOS pipeline and let onChange's Android branch
          // (which receives the inserted text via inputType='insertText') drive
          // the rawDigits update. Without this guard, the non-digit/non-era fall
          // through at the bottom would either swallow the input (preventDefault)
          // or leave rawDigits stale.
          if ((e.key === 'Unidentified' || e.keyCode === 229) && !e.code) {
            return;
          }
          const isComposing = (e.nativeEvent as KeyboardEvent | undefined)?.isComposing;
          if (isComposing) {
            composingRef.current = true;
            // Block non-digit, non-era characters during IME composition to prevent accented letters.
            const isAllowedIMEKey =
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'Enter' ||
              e.key === 'Tab' ||
              e.key === 'Escape' ||
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight';
            if (!isAllowedIMEKey) {
              e.preventDefault();
            }
            return;
          }
          if (composingRef.current) composingRef.current = false;
          // Let the user move the caret normally inside the input.
          if (
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Home' ||
            e.key === 'End'
          ) {
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            // e.stopPropagation();
            commitRawInput();
            setOpen(false);
            if (onEnter) onEnter();
            setTimeout(() => {
              const el = inputRef.current?.input ?? inputRef.current?.nativeElement;
              el?.blur?.();
            }, 0);
            return;
          }
          if (e.key === 'Tab' || e.key === 'Escape') {
            setOpen(false);
            return;
          }
          // When in "delete mode" (user deleted in middle): no validation, only commit on Enter/blur
          if (open && inputBuffer !== null) {
            return;
          }
          const isIMEKey = e.key === 'Process' || e.key === 'Unidentified';
          const isDeleteKey = e.key === 'Backspace' || e.key === 'Delete';
          if (composingRef.current && !isIMEKey && !isDeleteKey) return;
          if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            const inputEl = e.target as HTMLInputElement;
            const hasFullSelection =
              inputEl.selectionStart !== inputEl.selectionEnd &&
              inputEl.selectionStart === 0 &&
              inputEl.selectionEnd === inputEl.value.length;
            if (hasFullSelection) {
              setRawDigits('');
              setHideSuffix(false);
              if (value?.trim()) onChange?.('');
              return;
            }
            // Build the current digit buffer: from committed value if no rawDigits yet, else from rawDigits.
            let current: string;
            if (rawDigits === null) {
              if (!value?.trim()) return;
              const existing = dateToRawDigits(date, picker);
              if (!existing) return;
              current = existing;
            } else {
              if (rawDigits.length === 0) return;
              current = rawDigits;
            }
            // At a suffix boundary (length 3/5/7), drop the whole completed segment
            // (digits + 年/月/日) in one keystroke so "昭和26年01月01日" → "昭和26年01月" → "昭和26年" → "昭和" → "".
            const removeCount = isAtSuffixBoundary(current.length, picker) ? 2 : 1;
            setRawDigits(current.slice(0, Math.max(0, current.length - removeCount)));
            setHideSuffix(false);
            return;
          }
          // Tab: let focus move, but close the popup.
          if (e.key === 'Tab') {
            setOpen(false);
            return;
          }
          // Escape: close the popup (do not prevent default).
          if (e.key === 'Escape') {
            setOpen(false);
            return;
          }
          const inputEl = e.target as HTMLInputElement;
          const hasFullSelection =
            inputEl.selectionStart !== inputEl.selectionEnd &&
            inputEl.selectionStart === 0 &&
            inputEl.selectionEnd === inputEl.value.length;
          const isLockedCommitted = rawDigits === null && !!value?.trim();
          // Handle Latin era shortcut keys: M=明治(1), T=大正(2), S=昭和(3), H=平成(4), R=令和(5)
          // Shift+1..5: !@#$% (US) or !"#$% (JIS Japanese) map to era digits (1..5)
          const ERA_KEY_MAP: Record<string, string> = {
            m: '1',
            M: '1',
            '!': '1', // Shift+1 (US/JIS)
            t: '2',
            T: '2',
            '@': '2', // Shift+2 (US)
            s: '3',
            S: '3',
            '#': '3', // Shift+3 (US/JIS)
            h: '4',
            H: '4',
            $: '4', // Shift+4 (US/JIS)
            r: '5',
            R: '5',
            '%': '5', // Shift+5 (US/JIS)
          };
          // IME (e.g. Japanese) may send Process/Unidentified; use e.code so behaviour matches physical key
          const ERA_CODE_MAP: Record<string, string> = {
            KeyM: '1',
            KeyT: '2',
            KeyS: '3',
            KeyH: '4',
            KeyR: '5',
          };
          // Shift+Digit1..5 with IME (Japanese): e.key is Process/Unidentified, e.code is Digit1..Digit5
          const SHIFT_DIGIT_ERA_MAP: Record<string, string> = {
            Digit1: '1',
            Digit2: '2',
            Digit3: '3',
            Digit4: '4',
            Digit5: '5',
          };
          const eraDigit: string | undefined =
            ERA_KEY_MAP[e.key] ??
            (e.key === 'Process' || e.key === 'Unidentified'
              ? ((e.shiftKey ? SHIFT_DIGIT_ERA_MAP[e.code] : undefined) ?? ERA_CODE_MAP[e.code])
              : undefined);
          if (eraDigit != null) {
            // If a full valid value is already committed, block typing more unless user clears/overwrites selection.
            if (isLockedCommitted && !hasFullSelection) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            // Mark this key as handled so the Android onChange branch doesn't
            // double-process if the input event still fires (some browsers do
            // even after preventDefault on keydown).
            justHandledByKeyDownRef.current = true;
            // setTimeout (not queueMicrotask) — the input event is dispatched
            // in the same task as keydown, and microtasks fire BETWEEN event
            // listeners, which would clear the flag before onChange could see
            // it. setTimeout(0) defers to the next macrotask, after onChange.
            setTimeout(() => {
              justHandledByKeyDownRef.current = false;
            }, 0);
            setHideSuffix(false);
            setRawDigits(prev => {
              const current = (isLockedCommitted || hasFullSelection ? '' : prev) ?? '';
              const digitsOnly = current.replace(/[^0-9]/g, '');
              const nextDigits = digitsOnly + eraDigit;
              if (nextDigits.length > maxDigits) return prev;
              // Validate year/month/day boundaries even when using era shortcut keys (typing tttt...)
              if (!canAppendWarekiDigit(digitsOnly, eraDigit, picker)) return prev;
              return current + eraDigit;
            });
            return;
          }
          // Digit 0-9: e.key normally; when IME sends Process/Unidentified use e.code (Digit0..Digit9, Numpad0..Numpad9)
          const DIGIT_CODE_MAP: Record<string, string> = {
            Digit0: '0',
            Digit1: '1',
            Digit2: '2',
            Digit3: '3',
            Digit4: '4',
            Digit5: '5',
            Digit6: '6',
            Digit7: '7',
            Digit8: '8',
            Digit9: '9',
            Numpad0: '0',
            Numpad1: '1',
            Numpad2: '2',
            Numpad3: '3',
            Numpad4: '4',
            Numpad5: '5',
            Numpad6: '6',
            Numpad7: '7',
            Numpad8: '8',
            Numpad9: '9',
          };
          const digitToAppend = /^\d$/.test(e.key)
            ? e.key
            : e.key === 'Process' || e.key === 'Unidentified'
              ? DIGIT_CODE_MAP[e.code]
              : null;
          if (digitToAppend == null) {
            // Block non-digit, non-era printable characters (a,b,c,%,&,...) from being typed
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
            }
            return;
          }
          // If a full valid value is already committed, block typing more unless user clears/overwrites selection.
          if (isLockedCommitted && !hasFullSelection) {
            e.preventDefault();
            return;
          }
          // Append the digit (block if year/month/day would become invalid)
          e.preventDefault();
          // Mark as handled so Android onChange branch doesn't double-process.
          justHandledByKeyDownRef.current = true;
          // Mozc/Hiragana on Ubuntu can still dispatch composition/input events
          // after keydown.preventDefault(). Keep the flag alive until the next
          // macrotask; a microtask clears too early and the same physical key is
          // processed twice.
          setTimeout(() => {
            justHandledByKeyDownRef.current = false;
          }, 0);
          setHideSuffix(false);
          const digitsOnly = (
            (isLockedCommitted || hasFullSelection
              ? ''
              : rawDigits ?? (!value?.trim() ? defaultEraRawDigits : null)) ?? ''
          ).replace(/[^0-9]/g, '');
          let nextDigits = digitsOnly + digitToAppend;
          if (nextDigits.length > maxDigits) return;
          if (!canAppendWarekiDigit(digitsOnly, digitToAppend, picker)) return;

          // Auto-pad month: 1st month digit (pos 3) is 2-9 → prepend "0" (e.g. "3" → "03")
          if (nextDigits.length === 4 && picker !== 'year') {
            const monthFirstDigit = parseInt(nextDigits[3], 10);
            if (monthFirstDigit >= 2 && monthFirstDigit <= 9 && isMonthValid(monthFirstDigit)) {
              nextDigits = nextDigits.slice(0, 3) + '0' + nextDigits[3];
            }
          }
          // Auto-pad day: 1st day digit (pos 5) is 4-9 → prepend "0" (e.g. "5" → "05")
          if (nextDigits.length === 6 && picker === 'day') {
            const dayFirstDigit = parseInt(nextDigits[5], 10);
            if (dayFirstDigit >= 4 && dayFirstDigit <= 9 && isDayValid(dayFirstDigit)) {
              nextDigits = nextDigits.slice(0, 5) + '0' + nextDigits[5];
            }
          }

          if (nextDigits.length > maxDigits) return;
          setRawDigits(() => nextDigits);
          if (nextDigits.length === maxDigits) {
            const parsed = strictParseWarekiShorthand(nextDigits);
            if (parsed) {
              setDate(parsed);
              setViewDate(new Date(parsed));
              // Keep the popup open and aligned to the newly-valid date.
              setCurrentView(effectiveStartView);
              const formatted = formatDate(parsed, format);
              onChange?.(formatted);
              setRawDigits(null);
              setHideSuffix(false);
              openPicker();
            }
          }
        }}
        className="!h-[40px] w-full !px-[8px] !py-[7px]"
      />
      {typeof document !== 'undefined' && createPortal(pickerContent, document.body)}
    </div>
  );
});

RA2NDatePicker.displayName = 'RA2NDatePicker';

export default RA2NDatePicker;
